import { useState } from 'react';
import { DataGrid, GridRowsProp, GridColDef, GridCellParams } from '@mui/x-data-grid';
import { Grid, IconButton, Typography, Checkbox } from '@material-ui/core';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import { userTypesString } from '../../app/enums';
import { EditUserDialog } from '../../components/Users/EditUserDialog';
import { defaultUserType } from '../../pages/Users';
import { useUserContext } from '../../context/UserContext';
import { Location } from '../../models/location.interface';
import { UserType } from '../../models/user.interface';
import { isEmpty } from "lodash";
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';

const useStyles = makeStyles((theme: Theme) =>
  createStyles ({
    dataGridRoot:{
      border: 0,
      '& .header' : {
        background: theme.palette.background.default,
        color: theme.palette.primary.dark,
        fontWeight: '600', 
        fontSize: 16,
        padding: theme.spacing(0),
      },
      '& .MuiDataGrid-menuIconButton': {
        color: theme.palette.primary.dark,
        size: 'small',
        padding: theme.spacing(0),
      },
      '& .MuiDataGrid-sortIcon': {
        color: theme.palette.primary.dark,
        size: 'small',
        padding: theme.spacing(0),
      },
      headerHeight: 45,
      rowHeight: 40,
      pageSize: 10,
    },
    margin: {
      margin: theme.spacing(1),
    },
  }),
);

interface IUserGridProps {
  locationList: Location[]
  reloadList: () => void
}

export const UsersGrid = ({ locationList, reloadList }: IUserGridProps) => {
  const classes = useStyles();
  const [ edit, setEdit ] = useState(false);
  const [ editUser, setEditUser ] = useState<UserType>(defaultUserType);
  const { state } = useUserContext();
  const [ page, setPage ] = useState(0)
  
  const getLocationString = (params: any) => {
    if(params.value === '')
      return ''

    const paramValue = params.value as Location[]
    const locString = isEmpty(paramValue) ? '' : paramValue[0].name 

    return `${locString}`
  }

  const columns: GridColDef[] = [
    { 
      field: 'userName', 
      headerClassName: 'header', 
      headerAlign: 'center',
      headerName: 'User Name', 
      flex: 1.5
    },
    { 
      field: 'name', 
      headerClassName: 'header', 
      headerAlign: 'center',
      headerName: 'Name', 
      flex: 1
    },
    { 
      field: 'email', 
      headerClassName: 'header', 
      headerAlign: 'center',
      headerName: 'Email', 
      flex: 1
    },
    { 
      field: 'userType', 
      headerClassName: 'header', 
      headerAlign: 'center',
      headerName: 'User Type', 
      flex: 1,
      renderCell: (params: GridCellParams) => (
        <Typography variant="inherit" display="inline">  
          {userTypesString[params.value as keyof typeof userTypesString]}
        </Typography>    
      ),
    },
    { 
      field: 'location', 
      headerClassName: 'header', 
      headerAlign: 'center',
      headerName: 'Location', 
      flex: 1,
      valueGetter: getLocationString,
    }, 
    { 
      field: 'enabled', 
      headerClassName: 'header', 
      headerAlign: 'center',
      headerName: 'Enabled', 
      type: "boolean",
      flex: 1,
      renderCell: (params: GridCellParams) => (
        <Grid><Checkbox disabled checked={getEnabled(params)}/> </Grid>
      ),
    },
    { 
      field: 'edit', 
      headerClassName: 'header', 
      headerAlign: 'center',
      headerName: 'Edit', 
      sortable: false,
      disableColumnMenu: true,
      flex: .75,
      renderCell: (params: GridCellParams) => (
        <Grid container 
            justifyContent="center" 
            alignItems="center" 
            style={{ minHeight: '10vh' }}
          >
          <IconButton
            aria-label="delete"
            className={classes.margin}
            onClick={() => showEditDialog(params)}
            color="primary"
          >
              <EditOutlinedIcon fontSize="medium" />
            </IconButton>
        </Grid>
      ),
    },
  ];

  function getEnabled(params: GridCellParams) {
    return params.value ? true : false
  }

  const rows: GridRowsProp = state.usersList.map((user, index) => ({ 
      id: user.id,
      userName: user.userName,
      userType: user.userType,
      email: user.email,
      enabled: user.enabled,
      name: `${user.firstName} ${user.lastName}`,
      lastName: user.lastName,
      firstName: user.firstName,
      settings: user.settings,
      authenticated: user.authenticated,
      location: isEmpty(user.locations) ? '' : user.locations
    }));

  const showEditDialog = (params: GridCellParams) => {
    let user: UserType = {
      id: params.row.id,
      userName: params.row.userName,
      userType: params.row.userType,
      email: params.row.email,
      enabled: params.row.enabled,
      lastName: params.row.lastName,
      firstName: params.row.firstName,
      settings: params.row.settings,
      authenticated: params.row.authenticated,
      locations: params.row.location
    }

    setEditUser(user)
    setEdit(true)
  }

  const handleEditClose = () => {
    setEdit(false);
    setEditUser(defaultUserType)
    reloadList()
  };

  return (
    <>
      <div style={{ height: 500, width: '100%' }} className={classes.dataGridRoot}>
        <DataGrid 
          rows={rows} 
          columns={columns} 
          pageSize={10} 
          rowHeight={40} 
          headerHeight={45}
          page={page}
          rowsPerPageOptions={[10]}
          onPageChange={(newPage) => setPage(newPage)}
        />
      </div>
      <EditUserDialog
        key={editUser.id}
        open={edit}
        onClose={handleEditClose} 
        user={editUser}
        locationList={locationList}
      /> 
    </>
  );
};

export default UsersGrid;
